import { useState, useRef, useEffect, createContext, useContext } from 'react';
import { DropdownProps, DropdownItemProps } from '../../types/components';

const DropdownContext = createContext<{ close: () => void }>({ close: () => {} });

export function Dropdown({ trigger, children, align = 'right', className = '' }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const alignmentClass = align === 'left' ? 'left-0' : 'right-0';

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div onClick={() => setIsOpen(!isOpen)}>
        {trigger}
      </div>

      {isOpen && (
        <div className={`absolute ${alignmentClass} mt-2 min-w-[14rem] bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50`}>
          <DropdownContext.Provider value={{ close: () => setIsOpen(false) }}>
            {children}
          </DropdownContext.Provider>
        </div>
      )}
    </div>
  );
}

export function DropdownItem({ icon, children, href, onClick, variant = 'default', className = '' }: DropdownItemProps) {
  const { close } = useContext(DropdownContext);

  const variantClasses = {
    default: 'text-gray-700 hover:bg-gray-100 focus:bg-gray-100',
    danger: 'text-red-600 hover:bg-red-50 focus:bg-red-50'
  };

  const handleClick = () => {
    onClick?.();
    close();
  };

  const baseClass = `w-full text-left px-4 py-2 flex items-center space-x-3 focus:outline-none ${variantClasses[variant]} ${className}`;
  const content = (
    <>
      {icon && <span className="w-4 h-4 flex-shrink-0 [&>svg]:w-4 [&>svg]:h-4" aria-hidden="true">{icon}</span>}
      <span>{children}</span>
    </>
  );

  if (href) {
    return (
      <a href={href} onClick={handleClick} className={baseClass}>
        {content}
      </a>
    );
  }

  return (
    <button onClick={handleClick} className={baseClass}>
      {content}
    </button>
  );
}

export function DropdownDivider() {
  return <div className="border-t border-gray-200 my-2" />;
}
